import * as React from "react";
import { RouterLink as Link } from "../../router";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../table";
import { Card, CardContent } from "../../card";
import { Button } from "../../button";
import { Input } from "../../input";
import { SearchInput } from "../../search-input";
import { Icon } from "../../icon";
import { SectionHeader } from "../list-card";
import { DataPager } from "../data-pager";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../dialog";
import { cn } from "../../utils";
import { useApi, apiCall } from "../../api/client";
import { rows as rowsContract, type ColumnMetaDto } from "../../api/contracts/database/rows";
import type { RunResultDto } from "../../api/contracts/database/sql";
import { ColumnEditor } from "./column-editor";

function firstError(res: RunResultDto): string | null {
  for (const s of res.statements) if (s.error) return s.error;
  return null;
}

/** Coerce an editor's string input to a cell value typed by the column. */
function coerce(value: string, type: string): string | number | null {
  if (value === "") return null;
  const t = type.toUpperCase();
  if (/INT|REAL|NUMERIC|DOUBLE|FLOAT/.test(t)) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return value;
}

function displayCell(v: unknown): React.ReactNode {
  if (v === null || v === undefined) {
    return <span className="text-muted-foreground/50 italic">NULL</span>;
  }
  return String(v);
}

/**
 * Spreadsheet-style grid for one table: browse (server-paginated, sortable,
 * per-column + free-text filter), inline cell edit, insert, delete, and the
 * structural DDL actions (add/drop column, drop table). Read-only until
 * `writeMode` is on — every write affordance is disabled otherwise. Composes
 * the Table atoms (DataTable is read-only, so this is a new block). PRD §5.2.
 */
export function DatabaseGrid({
  table,
  writeMode,
  initialFilters,
  title,
  headerActions,
}: {
  table: string;
  writeMode: boolean;
  initialFilters?: Record<string, string>;
  /**
   * Integrated card-header title (e.g. the `<TableSwitcher trigger="title">`),
   * so the table picker reads as the grid's own header rather than a control
   * floating above it. Falls back to the mono table name.
   */
  title?: React.ReactNode;
  /**
   * Extra header actions (e.g. the write-mode toggle + "Ask AI") placed left of
   * the grid's own Insert / Add-column buttons in the integrated header.
   */
  headerActions?: React.ReactNode;
}) {
  const [page, setPage] = React.useState(1);
  const [sort, setSort] = React.useState<string | null>(null);
  const [dir, setDir] = React.useState<"asc" | "desc">("asc");
  const [q, setQ] = React.useState("");
  const [filters, setFilters] = React.useState<Record<string, string>>(
    initialFilters ?? {},
  );
  const [editing, setEditing] = React.useState<{ row: number; col: string } | null>(null);
  const [editValue, setEditValue] = React.useState("");
  const [inserting, setInserting] = React.useState(false);
  const [actionError, setActionError] = React.useState<string | null>(null);

  // Reset paging/sort/filters when the table changes.
  React.useEffect(() => {
    setPage(1);
    setSort(null);
    setDir("asc");
    setQ("");
    setFilters(initialFilters ?? {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table]);

  const activeFilters = React.useMemo(() => {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(filters)) if (v !== "") out[k] = v;
    return out;
  }, [filters]);

  const { data, isLoading, error, mutate } = useApi(rowsContract.list, {
    query: {
      table,
      page,
      sort: sort ?? undefined,
      dir,
      q: q || undefined,
      filters: Object.keys(activeFilters).length
        ? JSON.stringify(activeFilters)
        : undefined,
    },
  });

  const columns: ColumnMetaDto[] = data?.columns ?? [];
  const gridRows: unknown[][] = data?.rows ?? [];
  const identity = data?.identity ?? [];
  const editable = (data?.editable ?? false) && writeMode;
  const total = data?.total ?? 0;
  const pageSize = data?.page_size ?? 100;
  const totalPages = total === 0 ? 1 : Math.ceil(total / pageSize);

  // Clamp to a valid page when the row set shrinks (a filter narrows results)
  // below the current page — otherwise we'd sit on an empty page.
  React.useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  function colIndex(name: string): number {
    return columns.findIndex((c) => c.name === name);
  }

  function toCell(v: unknown): string | number | boolean | null {
    if (v === null || v === undefined) return null;
    if (typeof v === "number" || typeof v === "string" || typeof v === "boolean") {
      return v;
    }
    return String(v);
  }

  function buildIdentity(row: unknown[]): Record<string, string | number | boolean | null> {
    const idObj: Record<string, string | number | boolean | null> = {};
    for (const idCol of identity) {
      const i = colIndex(idCol);
      idObj[idCol] = i >= 0 ? toCell(row[i]) : null;
    }
    return idObj;
  }

  function toggleSort(col: string) {
    if (sort === col) setDir(dir === "asc" ? "desc" : "asc");
    else {
      setSort(col);
      setDir("asc");
    }
    setPage(1);
  }

  function startEdit(rowIdx: number, col: ColumnMetaDto, current: unknown) {
    if (!editable) return;
    if (identity.includes(col.name) && data?.usesRowid) return; // don't edit rowid
    setEditing({ row: rowIdx, col: col.name });
    setEditValue(current === null || current === undefined ? "" : String(current));
  }

  async function commitEdit(row: unknown[], col: ColumnMetaDto) {
    const value = coerce(editValue, col.type);
    setEditing(null);
    setActionError(null);
    try {
      const res = await apiCall(rowsContract.update, {
        body: { table, identity: buildIdentity(row), set: { [col.name]: value } },
      });
      const err = firstError(res);
      if (err) setActionError(err);
      await mutate();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "update failed");
    }
  }

  async function deleteRow(row: unknown[]) {
    setActionError(null);
    try {
      const res = await apiCall(rowsContract.delete, {
        body: { table, identity: buildIdentity(row) },
      });
      const err = firstError(res);
      if (err) setActionError(err);
      await mutate();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "delete failed");
    }
  }

  function fkHref(col: ColumnMetaDto, value: unknown): string | null {
    if (!col.fkTable || !col.fkColumn || value === null || value === undefined) {
      return null;
    }
    const params = new URLSearchParams({
      fkcol: col.fkColumn,
      fkval: String(value),
    });
    return `/database/editor/${encodeURIComponent(col.fkTable)}?${params.toString()}`;
  }

  const colCount = columns.length + (writeMode ? 1 : 0) || 1;

  return (
    <div className="flex flex-col gap-3">
      {!data?.editable && writeMode ? (
        <p className="type-subtext text-muted-foreground">
          This object has no primary key or rowid (view, virtual, or WITHOUT
          ROWID table) — rows can be read but not edited inline. Use the SQL
          editor to mutate it.
        </p>
      ) : null}

      {actionError ? (
        <p className="type-body text-destructive">{actionError}</p>
      ) : null}
      {error ? (
        <p className="type-body text-destructive">
          {error instanceof Error ? error.message : "failed to load rows"}
        </p>
      ) : null}

      {/* One integrated grid Card: the table picker is the header title, the
          mode + assistant + structural actions ride the header, and the pager
          is a flush footer band — never a control floating above or below. */}
      <Card className="gap-0 overflow-hidden border border-border py-0">
        <CardContent className="px-0">
          <SectionHeader
            title={
              title ?? (
                <span className="font-mono type-body font-medium text-foreground">
                  {table}
                </span>
              )
            }
            count={total}
            subtitle={
              editable ? "Double-click a cell to edit." : undefined
            }
            action={
              <div className="flex flex-wrap items-center gap-2">
                {headerActions}
                {writeMode ? (
                  <>
                    <Button
                      size="sm"
                      onClick={() => setInserting(true)}
                      disabled={!data?.editable}
                    >
                      <Icon name="plus" size="sm" /> Insert row
                    </Button>
                    <ColumnEditor
                      table={table}
                      columns={columns
                        .filter((c) => c.name !== "rowid")
                        .map((c) => c.name)}
                      onChanged={() => mutate()}
                    />
                  </>
                ) : null}
              </div>
            }
          />
          <div className="border-b border-border px-6 py-2.5">
            <SearchInput
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
              placeholder="Filter rows (free text across text columns)…"
              width="auto"
              className="w-72"
            />
          </div>
          <Table>
          <TableHeader>
            <TableRow>
              {writeMode ? <TableHead className="w-10" /> : null}
              {columns.map((c) => {
                const isSorted = sort === c.name;
                return (
                  <TableHead key={c.name}>
                    <button
                      type="button"
                      onClick={() => toggleSort(c.name)}
                      className="flex items-center gap-1 text-foreground transition-opacity hover:opacity-60"
                    >
                      <span className="font-medium">{c.name}</span>
                      {c.pk > 0 ? (
                        <Icon name="key-round" size="xs" className="text-muted-foreground/60" />
                      ) : null}
                      {c.fkTable ? (
                        <Icon name="link" size="xs" className="text-muted-foreground/60" />
                      ) : null}
                      {isSorted ? (
                        <Icon
                          name={dir === "asc" ? "chevron-up" : "chevron-down"}
                          size="xs"
                        />
                      ) : null}
                    </button>
                    <span className="block type-subtext font-normal text-muted-foreground/60">
                      {c.type || "—"}
                    </span>
                  </TableHead>
                );
              })}
            </TableRow>
            {/* per-column filter row */}
            <TableRow>
              {writeMode ? <TableHead className="w-10 p-1" /> : null}
              {columns.map((c) => (
                <TableHead key={c.name} className="p-1">
                  <Input
                    value={filters[c.name] ?? ""}
                    onChange={(e) => {
                      setFilters((f) => ({ ...f, [c.name]: e.target.value }));
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") setPage(1);
                    }}
                    placeholder="="
                    className="h-7 type-subtext"
                  />
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && gridRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={colCount}
                  className="text-center text-muted-foreground"
                >
                  Loading…
                </TableCell>
              </TableRow>
            ) : gridRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={colCount}
                  className="text-center text-muted-foreground"
                >
                  No rows.
                </TableCell>
              </TableRow>
            ) : (
              gridRows.map((row, ri) => (
                <TableRow key={ri}>
                  {writeMode ? (
                    <TableCell className="w-10 p-1">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => deleteRow(row)}
                        disabled={!data?.editable}
                        aria-label="Delete row"
                      >
                        <Icon name="trash-2" size="xs" className="text-muted-foreground" />
                      </Button>
                    </TableCell>
                  ) : null}
                  {columns.map((c, ci) => {
                    const value = row[ci];
                    const isEditing =
                      editing && editing.row === ri && editing.col === c.name;
                    const href = fkHref(c, value);
                    return (
                      <TableCell
                        key={c.name}
                        onDoubleClick={() => startEdit(ri, c, value)}
                        className={cn(editable && "cursor-text")}
                      >
                        {isEditing ? (
                          <Input
                            autoFocus
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onBlur={() => commitEdit(row, c)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") commitEdit(row, c);
                              if (e.key === "Escape") setEditing(null);
                            }}
                            className="h-7 type-subtext"
                          />
                        ) : href ? (
                          <Link
                            href={href}
                            className="text-foreground underline decoration-dotted underline-offset-2 hover:decoration-solid"
                          >
                            {displayCell(value)}
                          </Link>
                        ) : (
                          <span className="text-foreground">{displayCell(value)}</span>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
          </Table>
          {/* Integrated pager — flush footer band inside the Card; self-hides
              when there's a single page (no detached footer). */}
          <DataPager
            page={page}
            totalPages={totalPages}
            total={total}
            wrapped
            onPageChange={(p) => setPage(p)}
          />
        </CardContent>
      </Card>

      <InsertRowDialog
        table={table}
        columns={columns}
        open={inserting}
        onClose={() => setInserting(false)}
        onDone={() => mutate()}
      />
    </div>
  );
}

function InsertRowDialog({
  table,
  columns,
  open,
  onClose,
  onDone,
}: {
  table: string;
  columns: ColumnMetaDto[];
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const editable = columns.filter((c) => c.name !== "rowid");
  const [values, setValues] = React.useState<Record<string, string>>({});
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setValues({});
      setError(null);
    }
  }, [open]);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const body: Record<string, string | number | null> = {};
      for (const c of editable) {
        const raw = values[c.name];
        if (raw === undefined || raw === "") continue; // omit → use DB default
        body[c.name] = coerce(raw, c.type);
      }
      const res = await apiCall(rowsContract.insert, { body: { table, values: body } });
      const err = firstError(res);
      if (err) {
        setError(err);
        return;
      }
      onDone();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "insert failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? onClose() : null)}>
      <DialogContent className="max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Insert row into {table}</DialogTitle>
          <DialogDescription>
            Leave a field blank to use its column default (or NULL).
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          {editable.map((c) => (
            <div key={c.name} className="flex flex-col gap-1">
              <span className="type-subtext text-muted-foreground">
                {c.name}{" "}
                <span className="text-muted-foreground/50">
                  {c.type}
                  {c.notnull ? " · not null" : ""}
                  {c.pk > 0 ? " · pk" : ""}
                </span>
              </span>
              <Input
                value={values[c.name] ?? ""}
                onChange={(e) => setValues((v) => ({ ...v, [c.name]: e.target.value }))}
              />
            </div>
          ))}
          {error ? <p className="type-subtext text-destructive">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy}>
            {busy ? "Inserting…" : "Insert row"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
